import { useEffect, useState } from 'react';
import { supabase, Page, SocialLink, HeaderSettings, MenuItem, FooterSettings } from '../lib/supabase';
import { Menu, X, ChevronRight } from 'lucide-react';

export default function PrivacyPolicyPage() {
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [headerSettings, setHeaderSettings] = useState<HeaderSettings | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [footerSettings, setFooterSettings] = useState<FooterSettings | null>(null);

  useEffect(() => {
    loadPage();
    loadSocialLinks();
    loadHeaderSettings();
    loadMenuItems();
    loadFooterSettings();
  }, []);

  useEffect(() => {
    updateMetaTags();
  }, [page]);

  function updateMetaTags() {
    if (!page) return;

    document.title = page.seo_title || page.title;

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', page.seo_description || '');
    } else if (page.seo_description) {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = page.seo_description;
      document.head.appendChild(meta);
    }

    if (page.seo_keywords) {
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', page.seo_keywords);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'keywords';
        meta.content = page.seo_keywords;
        document.head.appendChild(meta);
      }
    }

    const metaRobots = document.querySelector('meta[name="robots"]');
    const robotsValue = `${page.seo_index ? 'index' : 'noindex'}, ${page.seo_follow ? 'follow' : 'nofollow'}`;
    if (metaRobots) {
      metaRobots.setAttribute('content', robotsValue);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = robotsValue;
      document.head.appendChild(meta);
    }
  }

  async function loadPage() {
    const { data } = await supabase
      .from('pages')
      .select('*')
      .eq('slug', 'privacy-policy')
      .eq('is_active', true)
      .maybeSingle();

    if (data) {
      setPage(data);
    }
    setLoading(false);
  }

  async function loadSocialLinks() {
    const { data } = await supabase
      .from('social_links')
      .select('*')
      .eq('is_active', true)
      .order('display_order');

    if (data) {
      setSocialLinks(data);
    }
  }

  async function loadHeaderSettings() {
    const { data } = await supabase
      .from('header_settings')
      .select('*')
      .limit(1)
      .maybeSingle();

    if (data) {
      setHeaderSettings(data);
    }
  }

  async function loadMenuItems() {
    const { data } = await supabase
      .from('menu_items')
      .select('*')
      .eq('is_active', true)
      .order('display_order');

    if (data) {
      setMenuItems(data);
    }
  }

  async function loadFooterSettings() {
    const { data } = await supabase
      .from('footer_settings')
      .select('*')
      .limit(1)
      .maybeSingle();

    if (data) {
      setFooterSettings(data);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-900 border-t-transparent"></div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-gray-600">Страница не найдена</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Open Sans', sans-serif" }}>
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-2 sm:py-3">
          <div className="max-w-7xl mx-auto px-4 flex justify-center items-center">
            <div className="flex gap-3 sm:gap-6 text-sm sm:text-base flex-wrap justify-center">
              {socialLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-gray-200 transition-colors whitespace-nowrap"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="py-1.5" style={{ backgroundColor: headerSettings?.background_color || '#FFE9FC' }}>
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-start gap-4" style={{ justifyContent: headerSettings?.content_alignment || 'space-between' }}>
              <div className="flex items-start gap-3">
                <a href={headerSettings?.logo_link || 'https://seo-nova.com'} className="flex-shrink-0">
                  <img
                    src={headerSettings?.logo_url || '/Logo 72 px_100Kb.jpg'}
                    alt={headerSettings?.logo_alt || 'Logo'}
                    title={headerSettings?.logo_title || ''}
                    style={{ height: `${headerSettings?.logo_height || 100}px` }}
                    className="w-auto"
                  />
                  {headerSettings?.logo_description && (
                    <span className="sr-only">{headerSettings.logo_description}</span>
                  )}
                </a>
                <div className="pt-1">
                  <h1
                    className="font-bold mb-0.5"
                    style={{
                      color: headerSettings?.title_color || '#ff0000',
                      fontFamily: headerSettings?.title_font_family || "'Open Sans', sans-serif",
                      fontSize: headerSettings?.title_font_size || '24px'
                    }}
                  >
                    {headerSettings?.title_text || 'Ольга Сеонова'}
                  </h1>
                  <p
                    className=""
                    style={{
                      fontSize: headerSettings?.subtitle_font_size || '14px',
                      color: headerSettings?.subtitle_color || '#374151'
                    }}
                  >
                    {headerSettings?.subtitle_text || 'Быстрые и простые рецепты'}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 mt-2"
                style={{ color: headerSettings?.menu_color || '#000066' }}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>

              <nav className="hidden lg:flex gap-6 items-center pt-8">
                {menuItems.map((item) => (
                  <a
                    key={item.id}
                    href={item.url}
                    className="font-semibold transition-colors"
                    style={{
                      color: headerSettings?.menu_color || '#000066',
                      fontSize: headerSettings?.menu_font_size || '16px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = headerSettings?.menu_hover_color || '#ef4444'}
                    onMouseLeave={(e) => e.currentTarget.style.color = headerSettings?.menu_color || '#000066'}
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>

            {mobileMenuOpen && (
              <nav className="lg:hidden mt-4 pb-4 border-t pt-4">
                <div className="flex flex-col gap-3">
                  {menuItems.map((item) => (
                    <a
                      key={item.id}
                      href={item.url}
                      className="font-semibold py-2"
                      style={{
                        color: headerSettings?.menu_color || '#000066',
                        fontSize: headerSettings?.menu_font_size || '16px'
                      }}
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </nav>
            )}
          </div>
        </div>
      </header>

      <div className="bg-gray-50 py-3 border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <a href="/" className="hover:text-red-600">Главная</a>
            <ChevronRight size={16} />
            <span className="text-gray-900">{page.title}</span>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <article className="bg-white rounded-lg shadow-sm p-8">
          <div
            className="prose max-w-none"
            style={{ color: '#000066', fontSize: '16px', lineHeight: '1.6' }}
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        </article>
      </main>

      <footer className="mt-12">
        <div className="py-4" style={{ backgroundColor: '#FFE9FC' }}>
          <div className="max-w-7xl mx-auto px-4">
            <p
              className="text-center text-sm"
              style={{ color: '#000066' }}
              dangerouslySetInnerHTML={{ __html: footerSettings?.copyright_text || '' }}
            />
          </div>
        </div>
      </footer>
    </div>
  );
}
